import React from 'react'

import { AppId } from '@inno-tools/container-bridge'
import { LockFullIcon } from '@inno-ui/icons'
import cn from 'classnames'
import { BadgeCount } from 'shared/components/BadgeCount/BadgeCount'
import { appConfigs } from 'shared/configs/appConfigs'
import { useTopDockStore } from 'shared/stores/topDockStore'
import { useUnreadCountStore } from 'shared/stores/unreadCountStore'
import { IframeAppInstance } from 'shared/types/index'

import { useAppButtonState } from '../../hooks/useAppButtonState'
import { AppIcon } from '../AppIcon'
import styles from './LaunchPadButton.module.scss'

interface LaunchPadButtonProps {
  app: IframeAppInstance
  onClick?: () => void
  isLocked?: boolean
  size?: 'medium' | 'large'
  variant?: 'light' | 'dark'
  isLoading?: boolean
}

export const LaunchPadButton: React.FC<LaunchPadButtonProps> = ({
  app,
  onClick,
  isLocked,
  size = 'medium',
  variant = 'light',
  isLoading
}) => {
  const buttonState = useAppButtonState(app)

  const { hasOpenWindow } = buttonState
  const pinnedAppIds = useTopDockStore(state => state.pinnedAppIds)
  const isPinned = pinnedAppIds.includes(app.id)
  const config = appConfigs[app.id as AppId]
  const unreadCount = useUnreadCountStore(state =>
    state.getCountForApp(app.id, config?.slug)
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && onClick && !isLocked) {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <div className={styles.appButtonContainer}>
      <div
        className={cn(styles.appIcon, { [styles.loading]: isLoading })}
        onClick={isLoading ? undefined : onClick}
        onKeyDown={isLoading ? undefined : handleKeyDown}
        role='button'
        tabIndex={isLocked || isLoading ? -1 : 0}
        aria-label={app.label}
        aria-disabled={isLocked}
        aria-busy={isLoading}
      >
        <div
          className={cn(styles.iconContainer, {
            [styles.large]: size === 'large',
            [styles.medium]: size === 'medium',
            [styles.light]: variant === 'light',
            [styles.dark]: variant === 'dark'
          })}
        >
          <div className={styles.iconContent}>
            {isLocked && !isLoading && (
              <LockFullIcon
                className={styles.lockIcon}
                color={size === 'medium' ? 'var(--grey-600)' : 'var(--white)'}
              />
            )}
            <div
              className={cn(styles.iconWrapper, {
                [styles.lockedApp]: isLocked,
                [styles.openApp]: hasOpenWindow,
                [styles.pinnedApp]: isPinned
              })}
            >
              <AppIcon app={app} className={styles.icon} />
              {isLoading && <div className={styles.shimmerOverlay} />}
              <BadgeCount count={unreadCount} />
            </div>
          </div>
          <div className={styles.label}>
            <span>{app.label}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
